import React from 'react';
import { 
  Box, Container, Typography, Stack, Chip, Grid, alpha, 
  Paper, Card, CardContent, Divider
} from '@mui/material';
import { 
  ShowChart, AccessTime, Diamond, 
  WorkspacePremium, LocalGasStation, OpenInNew as OpenInNewIcon 
} from '@mui/icons-material';
import { keyframes } from '@mui/system';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LivePricesSection = () => {
  // Direct Kitco URLs for each commodity
  const kitcoUrls = {
    gold: 'https://www.kitco.com/charts/livegold.html',
    silver: 'https://www.kitco.com/charts/livesilver.html',
    platinum: 'https://www.kitco.com/charts/liveplatinum.html',
    palladium: 'https://www.kitco.com/charts/livepalladium.html',
    diamond: 'https://www.kitco.com/commodities/diamond',
    oil: 'https://www.kitco.com/commodities/oil',
  };

  const commodities = [
    { name: 'GOLD', symbol: 'XAU', key: 'gold', icon: <Diamond sx={{ fontSize: 20 }} />, color: '#D4AF37', description: 'Safe-haven asset', priceUnit: '/oz' },
    { name: 'SILVER', symbol: 'XAG', key: 'silver', icon: <WorkspacePremium sx={{ fontSize: 20 }} />, color: '#C0C0C0', description: 'Industrial metal', priceUnit: '/oz' },
    { name: 'PLATINUM', symbol: 'XPT', key: 'platinum', icon: <Diamond sx={{ fontSize: 20 }} />, color: '#E5E4E2', description: 'Rare industrial', priceUnit: '/oz' },
    { name: 'PALLADIUM', symbol: 'XPD', key: 'palladium', icon: <WorkspacePremium sx={{ fontSize: 20 }} />, color: '#CED0D6', description: 'Auto-catalyst', priceUnit: '/oz' },
    { name: 'DIAMOND', symbol: 'DIA', key: 'diamond', icon: <Diamond sx={{ fontSize: 20 }} />, color: '#00BCD4', description: 'Precious stone', priceUnit: '/ct' },
    { name: 'OIL', symbol: 'WTI', key: 'oil', icon: <LocalGasStation sx={{ fontSize: 20 }} />, color: '#2C3E50', description: 'Energy commodity', priceUnit: '/bbl' },
  ];

  const handleCardClick = (url) => {
    window.open(url, '_blank');
  };

  return (
    <Box 
      sx={{ 
        py: 4, 
        bgcolor: '#F8F9FC',
        position: 'relative',
        borderTop: '1px solid rgba(212, 175, 55, 0.15)',
        borderBottom: '1px solid rgba(212, 175, 55, 0.15)',
      }}
    >
      <Container maxWidth="xl">
        <Stack 
          direction={{ xs: 'column', sm: 'row' }}
          sx={{ 
            mb: 3,
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' }
          }}
        >
          <Box>
            <Stack direction="row" sx={{ mb: 0.5, alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip 
                label="KITCO" 
                size="small"
                icon={<ShowChart sx={{ fontSize: 12 }} />}
                sx={{ 
                  bgcolor: alpha('#D4AF37', 0.12),
                  color: '#D4AF37',
                  fontWeight: 700,
                  fontSize: '0.6rem',
                  height: 22,
                }} 
              />
              <Chip 
                label="LIVE MARKET DATA"
                size="small"
                sx={{ 
                  bgcolor: alpha('#4caf50', 0.12),
                  color: '#4caf50',
                  fontSize: '0.55rem',
                  height: 18,
                  '& .MuiChip-label': { px: 1 }
                }} 
              />
            </Stack>
            
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                color: '#0a0a2a',
                fontSize: '1rem',
              }}
            >
              Real-Time Commodity Prices
            </Typography>
            <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), fontSize: '0.7rem' }}>
              Powered by Kitco
            </Typography>
          </Box>
          
          <Stack 
            direction="row" 
            spacing={1.5}
            sx={{ 
              alignItems: 'center',
              mt: { xs: 1, sm: 0 }
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                bgcolor: '#FFFFFF',
                borderRadius: 1.5,
                border: `1px solid ${alpha('#D4AF37', 0.2)}`,
              }}
            >
              <AccessTime sx={{ fontSize: 10, color: '#D4AF37' }} />
              <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.6), fontSize: '0.6rem' }}>
                Live Feed
              </Typography>
            </Paper>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box 
                sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  bgcolor: '#4caf50',
                  animation: 'pulse 2s infinite',
                }} 
              />
              <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), fontSize: '0.6rem' }}>
                Connected to Kitco
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Cards Grid */}
        <Grid container spacing={2.5}>
          {commodities.map((commodity, index) => {
            const kitcoUrl = kitcoUrls[commodity.key];
            
            return (
              <Grid item xs={12} sm={6} md={4} lg={2} key={commodity.key}>
                <Box
                  sx={{
                    animation: `${fadeInUp} 0.4s ease-out ${0.05 + index * 0.02}s forwards`,
                    opacity: 0,
                    height: '100%',
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      bgcolor: '#FFFFFF',
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      transition: 'all 0.3s ease',
                      border: `1px solid ${alpha(commodity.color, 0.15)}`,
                      height: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        borderColor: commodity.color,
                        boxShadow: `0 8px 20px ${alpha(commodity.color, 0.15)}`,
                        '& .icon-wrapper': {
                          background: `linear-gradient(135deg, ${commodity.color} 0%, ${alpha(commodity.color, 0.8)} 100%)`,
                          '& svg': { color: '#FFFFFF', transform: 'scale(1.05)' }
                        },
                        '& .open-link': {
                          opacity: 1,
                        }
                      },
                    }}
                    onClick={() => handleCardClick(kitcoUrl)}
                  >
                    {/* Decorative top bar */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: `linear-gradient(90deg, ${commodity.color}, ${alpha(commodity.color, 0.5)}, ${commodity.color})`,
                        zIndex: 1,
                      }}
                    />
                    
                    <CardContent sx={{ 
                      p: 2, 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column',
                      position: 'relative',
                      zIndex: 1,
                    }}>
                      {/* Header with Icon */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Box
                          className="icon-wrapper"
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '12px',
                            background: alpha(commodity.color, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {commodity.icon}
                        </Box>
                        
                        {/* External link indicator */}
                        <OpenInNewIcon 
                          className="open-link"
                          sx={{ 
                            fontSize: 16, 
                            color: commodity.color,
                            opacity: 0.5,
                            transition: 'opacity 0.3s ease',
                          }} 
                        />
                      </Box>
                      
                      {/* Title and Symbol */}
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0a0a2a', mb: 0.25, fontSize: '1rem' }}>
                        {commodity.name}
                      </Typography>
                      
                      <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), fontFamily: 'monospace', mb: 1, fontSize: '0.65rem' }}>
                        {commodity.symbol} • {commodity.priceUnit}
                      </Typography>
                      
                      {/* Description */}
                      <Typography variant="body2" sx={{ color: alpha('#0a0a2a', 0.6), mb: 1.5, fontSize: '0.75rem', lineHeight: 1.3 }}>
                        {commodity.description}
                      </Typography>
                      
                      <Divider sx={{ my: 1, borderColor: alpha(commodity.color, 0.1) }} />
                      
                      {/* Call to action */}
                      <Box sx={{ textAlign: 'center', mt: 'auto' }}>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: commodity.color, 
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                          }}
                        >
                          View Live on Kitco
                          <OpenInNewIcon sx={{ fontSize: 12 }} />
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Footer */}
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 1.5,
            bgcolor: '#FFFFFF',
            border: `1px solid ${alpha('#D4AF37', 0.15)}`,
            borderRadius: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: alpha('#0a0a2a', 0.5), fontSize: '0.6rem' }}>
            Real-time data provided by Kitco Metals Inc.
          </Typography>
          
          <ButtonLink 
            href="https://www.kitco.com" 
            text="Visit Kitco.com"
            color="#D4AF37"
          />
        </Paper>
      </Container>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
          }
        `}
      </style>
    </Box>
  );
};

// Helper component for external links
const ButtonLink = ({ href, text, color }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      color: color,
      fontSize: '0.6rem',
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
    }}
  >
    {text}
    <OpenInNewIcon sx={{ fontSize: 10 }} />
  </Box>
);

export default LivePricesSection;